import React, { useState, useEffect } from 'react';
import { Box, Paper, IconButton, Stack, Tooltip, Typography, Divider, TextField, Button } from '@mui/material';
import type { ChildrenAccordionProps } from '../../types';
import ChildRegistrationsList from './ChildRegistrationsList';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const ChildrenAccordion: React.FC<ChildrenAccordionProps> = ({ id, fname, lname, dob, gender, genders, isNew, onDelete, onUpdate }) => {
    const [expanded, setExpanded] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({ fname, lname, dob });
    const genderOption = genders.find((option) => option.toLowerCase() === gender.toLowerCase());
    const maxYearOld = dayjs().subtract(17, 'year');

    useEffect(() => {
        setEditForm({ fname, lname, dob });
    }, [fname, lname, dob]);

    function formatLastName(lastName: string) {
        const words = lastName.trim().split(' ');
        return words[words.length - 1];
    }

    const handleToggle = () => {
        if (!isNew && id && !isEditing) {
            setExpanded(!expanded);
        }
    };

    const handleEditClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setEditForm({ fname, lname, dob });
        setIsEditing(true);
    };

    const handleEditFieldChange = (key: 'fname' | 'lname' | 'dob', value: string | Dayjs | null) => {
        if (key === 'dob') {
            const formatted = value ? (typeof value === 'string' ? value : (value as Dayjs).format('MM/DD/YYYY')) : '';
            setEditForm(prev => ({ ...prev, dob: formatted }));
        } else {
            setEditForm(prev => ({ ...prev, [key]: (value as string) ?? '' }));
        }
    };

    const handleSaveEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!id || !onUpdate || !editForm.fname.trim() || !editForm.lname.trim() || !editForm.dob.trim()) return;
        onUpdate(id, { fname: editForm.fname.trim(), lname: editForm.lname.trim(), dob: editForm.dob });
        setIsEditing(false);
    };

    const handleCancelEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        setEditForm({ fname, lname, dob });
        setIsEditing(false);
    };

    return (
        <Paper 
            variant="outlined"
            sx={{
                position: 'relative',
                backgroundColor: isNew ? 'action.hover' : 'background.paper',
                borderRadius: 1,
                borderLeft: '3px solid #88B73E',
            }}
        >
            <Box 
                sx={{ 
                    p: 1.5,
                    cursor: (!isNew && id && !isEditing) ? 'pointer' : 'default',
                    '&:hover': (!isNew && id && !isEditing) ? {
                        backgroundColor: 'action.hover',
                    } : {},
                    transition: 'background-color 0.2s ease',
                }}
                onClick={handleToggle}
            >
                {isEditing ? (
                    <Box onClick={(e) => e.stopPropagation()}>
                        <Stack spacing={2} sx={{ pt: 0.5 }}>
                            <TextField
                                size="small"
                                fullWidth
                                label="First Name"
                                value={editForm.fname}
                                onChange={(e) => handleEditFieldChange('fname', e.target.value)}
                                inputProps={{ maxLength: 30 }}
                            />
                            <TextField
                                size="small"
                                fullWidth
                                label="Last Name"
                                value={editForm.lname}
                                onChange={(e) => handleEditFieldChange('lname', e.target.value)}
                                inputProps={{ maxLength: 30 }}
                            />
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Date of Birth"
                                    value={editForm.dob && dayjs(editForm.dob, 'MM/DD/YYYY').isValid() ? dayjs(editForm.dob, 'MM/DD/YYYY') : null}
                                    onChange={(date) => handleEditFieldChange('dob', date)}
                                    format="MM/DD/YYYY"
                                    maxDate={dayjs()}
                                    minDate={maxYearOld}
                                    slotProps={{
                                        textField: {
                                            variant: 'outlined',
                                            size: 'small',
                                            fullWidth: true,
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button size="small" variant="outlined" onClick={handleCancelEdit}>
                                    Cancel
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    disabled={!editForm.fname.trim() || !editForm.lname.trim() || !editForm.dob.trim()}
                                    onClick={handleSaveEdit}
                                    sx={{ backgroundColor: '#88B73E', color: 'white', '&:hover': { backgroundColor: '#7aa635' } }}
                                >
                                    Save
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                ) : (
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonIcon sx={{ fontSize: 20, color: 'action.active' }} />
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography variant="body1" component="div" fontWeight={600} noWrap>
                                {fname} {formatLastName(lname)}
                            </Typography>
                            <Stack direction="row" spacing={1.5} sx={{ mt: 0.25 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                    DOB: {dob}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                    {genderOption}
                                </Typography>
                            </Stack>
                        </Box>
                        {!isNew && id && onUpdate && (
                            <Tooltip title="Edit details">
                                <IconButton
                                    size="small"
                                    onClick={handleEditClick}
                                    sx={{ width: 32, height: 32 }}
                                >
                                    <EditIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {!isNew && id && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                    Show Registrations
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setExpanded(!expanded);
                                    }}
                                    sx={{
                                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.3s ease',
                                    }}
                                >
                                    <ExpandMoreIcon />
                                </IconButton>
                            </Box>
                        )}
                        {isNew && (
                            <Tooltip title="Delete">
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDelete();
                                    }}
                                    color="error"
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        '&:hover': {
                                            backgroundColor: 'error.light',
                                            color: 'error.contrastText'
                                        }
                                    }}
                                >
                                    <DeleteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                )}
            </Box>

            {!isNew && id && expanded && !isEditing && (
                <>
                    <Divider />
                    <Box sx={{ px: 1.5, pb: 1.5 }}>
                        <ChildRegistrationsList childId={id} />
                    </Box>
                </>
            )}
        </Paper>
    );
};

export default ChildrenAccordion;
